/**
 * Query Utilities for Access Control Hooks
 *
 * Provides helper functions for building API query strings
 */

/**
 * Build query string from params object
 * Handles arrays and nested objects (deep queries)
 *
 * Converts:
 * - { fields: ['id', 'name'] } -> fields[]=id&fields[]=name
 * - { filter: { _and: [{ policy: 'abc' }] } } -> filter[_and][0][policy]=abc
 * - { deep: { users: { _aggregate: { count: 'id' } } } } -> deep[users][_aggregate][count]=id
 */
export function buildQueryString(params: Record<string, unknown>): string {
    const parts: string[] = [];

    function addParam(key: string, value: unknown) {
        if (Array.isArray(value)) {
            value.forEach((v, index) => {
                if (typeof v === 'object' && v !== null) {
                    // Handle array of objects like filter[_and][0][policy]=value
                    addParam(`${key}[${index}]`, v);
                } else {
                    parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(String(v))}`);
                }
            });
        } else if (typeof value === 'object' && value !== null) {
            // Handle nested objects (deep queries, filters)
            for (const [subKey, subValue] of Object.entries(value)) {
                addParam(`${key}[${subKey}]`, subValue);
            }
        } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
        }
    }

    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
            addParam(key, value);
        }
    }

    return parts.join('&');
}

/**
 * Build filter query params in CMS format
 * 
 * Example:
 * buildFilterParams({ policy: 'abc', collection: 'users' })
 * -> filter[_and][0][policy]=abc&filter[_and][1][collection]=users
 */
export function buildFilterParams<T extends Record<string, unknown>>(filters: T): Record<string, unknown> {
    const andConditions: Record<string, unknown>[] = [];

    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null) {
            andConditions.push({ [key]: value });
        }
    }

    if (andConditions.length === 0) {
        return {};
    }

    return {
        filter: {
            _and: andConditions,
        },
    };
}
